import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Button, DatePicker, Form, Input, InputNumber, message, Modal, Select, Space, Table, Tag, Typography,
} from "antd";
import { BankOutlined, CheckCircleOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SafetyOutlined, SettingOutlined, StopOutlined } from "@ant-design/icons";
import moment from "moment";
import { hasPermission } from "../auth/permissions";
import {
  activateBankAccount, createBankAccount, deactivateBankAccount,
  getBankAccount, listBankAccounts, listBankNames, updateBankAccount,
} from "../services/bankAccountApi";
import { listAppParams } from "../services/appParamApi";
import ManagementPanel from "../components/management/ManagementPanel";
import AccountDetails from "../components/management/AccountDetails";

const EMPTY_PAGE = { content: [], page: 0, size: 20, totalElements: 0 };
const MONEY = new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND", maximumFractionDigits: 0 });
const DATE = new Intl.DateTimeFormat("vi-VN");
const asDate = (value) => value ? value.format("YYYY-MM-DD") : null;
const fromDate = (value) => value ? moment(value) : null;

function payloadFrom(values) {
  return {
    internalCode: values.internalCode.trim(),
    bankName: values.bankName.trim(),
    accountHolder: values.accountHolder.trim(),
    accountNumber: values.accountNumber.trim(),
    nickname: values.nickname?.trim() || null,
    currency: values.currency || "VND",
    openingBalance: Number(values.openingBalance || 0),
    managementStartDate: values.managementStartDate.format("YYYY-MM-DD"),
    accountType: values.accountType || null,
    branch: values.branch?.trim() || null,
    oldUsername: values.oldUsername?.trim() || null,
    newUsername: values.newUsername?.trim() || null,
    oldPassword: values.oldPassword || null,
    newPassword: values.newPassword || null,
    accountStatus: values.accountStatus || "ACTIVE",
    cardNumber: values.cardNumber || null,
    secretCode: values.secretCode || null,
    cardExpiry: asDate(values.cardExpiry),
    citizenId: values.citizenId || null,
    citizenIdExpiry: asDate(values.citizenIdExpiry),
    systemName: values.systemName?.trim() || null,
    usageStartDate: asDate(values.usageStartDate),
    email: values.email?.trim() || null,
    provider: values.provider?.trim() || null,
    departureDate: asDate(values.departureDate),
    information: values.information?.trim() || null,
  };
}

function displayAccountNumber(row) {
  return row.accountNumber || row.accountNumberMasked || "—";
}

export default function BankAccounts({ currentUser }) {
  const canManage = hasPermission(currentUser, "accounts:edit");
  const canDelete = hasPermission(currentUser, "accounts:delete");
  const [pageData, setPageData] = useState(EMPTY_PAGE);
  const [filters, setFilters] = useState({ query: "", active: "", accountType: "" });
  const [accountTypes, setAccountTypes] = useState([]);
  const [accountStatuses, setAccountStatuses] = useState([]);
  const [bankNames, setBankNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingRef = useRef(false);
  const loadSequence = useRef(0);
  const [form] = Form.useForm();
  const [viewing, setViewing] = useState(null);
  const canCredentials = ["SUPER_ADMIN", "ADMIN"].includes(currentUser?.role);

  async function load(page = 0, size = pageData.size || 20, nextFilters = filters) {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setLoadError("");
    try {
      const result = await listBankAccounts({ page, size, ...nextFilters });
      if (sequence === loadSequence.current) setPageData(result);
    } catch (error) {
      if (sequence === loadSequence.current) setLoadError(error.message || "Không thể tải danh sách tài khoản");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    load(0, 20, { query: "", active: "", accountType: "" });
    Promise.resolve(listBankNames()).then(value => setBankNames(value || [])).catch(() => setBankNames([]));
    listAppParams("ACCOUNT_STATUS").then(setAccountStatuses).catch(() => setAccountStatuses([]));
    listAppParams("ACCOUNT_TYPE").then(setAccountTypes).catch(() => setAccountTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function accountTypeName(code) {
    return accountTypes.find((item) => item.parCode === code)?.parName || code || "—";
  }

  function search() {
    load(0, pageData.size, filters);
  }

  function openCreate() {
    if (pendingRef.current) return;
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ currency: "VND", openingBalance: 0, managementStartDate: moment(), accountStatus: "ACTIVE" });
    setModalOpen(true);
  }

  async function openEdit(record) {
    if (pendingRef.current || !canManage) return;
    pendingRef.current = true;
    setEditing(record);
    setDetailLoading(true);
    setModalOpen(true);
    form.resetFields();
    try {
      const detail = await getBankAccount(record.id);
      form.setFieldsValue({
        ...detail,
        accountType: detail.accountType || undefined,
        managementStartDate: moment(detail.managementStartDate),
        cardExpiry: fromDate(detail.cardExpiry),
        citizenIdExpiry: fromDate(detail.citizenIdExpiry),
        usageStartDate: fromDate(detail.usageStartDate),
        departureDate: fromDate(detail.departureDate),
        cardNumber: undefined,
        secretCode: undefined,
        citizenId: undefined,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải chi tiết tài khoản");
      closeModal(true);
    } finally {
      pendingRef.current = false;
      setDetailLoading(false);
    }
  }

  function clearSensitiveState() {
    form.resetFields();
    setEditing(null);
  }

  function closeModal(force = false) {
    if (!force && pendingRef.current) return;
    setModalOpen(false);
    clearSensitiveState();
  }

  async function save(values) {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setSaving(true);
    try {
      const payload = payloadFrom(values);
      if (editing) await updateBankAccount(editing.id, payload);
      else await createBankAccount(payload);
      message.success(editing ? "Đã cập nhật tài khoản" : "Đã thêm tài khoản");
      closeModal(true);
      await load(pageData.page, pageData.size);
    } catch (error) {
      message.error(error.message || "Không thể lưu tài khoản");
    } finally {
      pendingRef.current = false;
      setSaving(false);
    }
  }

  function deactivate(record) {
    if (pendingRef.current) return;
    Modal.confirm({
      title: "Ngừng sử dụng tài khoản?",
      content: `${record.bankName} · ${displayAccountNumber(record)}`,
      okText: "Ngừng sử dụng",
      okButtonProps: { danger: true },
      cancelText: "Hủy",
      async onOk() {
        if (pendingRef.current) return;
        pendingRef.current = true;
        try {
          await deactivateBankAccount(record.id, "Ngừng sử dụng từ giao diện quản trị");
          message.success("Đã ngừng sử dụng tài khoản");
          await load(pageData.page, pageData.size);
        } catch (error) {
          message.error(error.message || "Không thể ngừng sử dụng tài khoản");
          throw error;
        } finally {
          pendingRef.current = false;
        }
      },
    });
  }

  function activate(record) {
    if (pendingRef.current) return;
    Modal.confirm({
      title: "Kích hoạt lại tài khoản?",
      content: `${record.bankName} · ${displayAccountNumber(record)}`,
      okText: "Kích hoạt lại",
      cancelText: "Hủy",
      async onOk() {
        if (pendingRef.current) return;
        pendingRef.current = true;
        try {
          await activateBankAccount(record.id, "Kích hoạt lại từ giao diện quản trị");
          message.success("Đã kích hoạt lại tài khoản");
          await load(pageData.page, pageData.size);
        } catch (error) {
          message.error(error.message || "Không thể kích hoạt lại tài khoản");
          throw error;
        } finally {
          pendingRef.current = false;
        }
      },
    });
  }

  const columns = [
    { title: "Tài khoản", key: "identity", width: 300, render: (_, row) => <div className="table-identity"><Button type="link" onClick={() => setViewing(row)}>{displayAccountNumber(row)}</Button><strong>{row.accountHolder}</strong><small>{row.internalCode} · {accountTypeName(row.accountType)}</small></div> },
    { title: "Ngân hàng / Chi nhánh", key: "bank", width: 230, render: (_, row) => <div className="table-identity"><strong>{row.bankName}</strong><small>{row.branch || "Chưa có chi nhánh"}</small></div> },
    { title: "Tình trạng tài khoản", dataIndex: "accountStatus", render: (code) => accountStatuses.find((item) => item.parCode === code)?.parName || code || "—" },
    { title: "Thiết bị", dataIndex: "deviceCode", width: 150, render: value => value ? <Tag>{value}</Tag> : <Typography.Text type="secondary">Chưa gắn thiết bị</Typography.Text> },
    { title: "Trạng thái", dataIndex: "active", render: (active) => <Tag color={active ? "green" : "default"}>{active ? "Đang dùng" : "Ngừng dùng"}</Tag> },
    {
      title: "Thao tác",
      key: "actions",
      width: 145, fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Button className="user-action-button" title="Chi tiết tài khoản" aria-label="Chi tiết tài khoản" icon={<EyeOutlined />} onClick={() => setViewing(record)} />
          {canManage && <Button className="user-action-button" title="Sửa" aria-label="Sửa" icon={<EditOutlined />} disabled={pendingRef.current} onClick={() => openEdit(record)} />}
          {canDelete && record.active && <Button className="user-action-button" title="Ngừng" aria-label="Ngừng" danger icon={<StopOutlined />} onClick={() => deactivate(record)} />}
          {canManage && !record.active && <Button className="user-action-button" title="Kích hoạt lại" aria-label="Kích hoạt lại" icon={<CheckCircleOutlined />} onClick={() => activate(record)} />}
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page upgrade-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Tài khoản ngân hàng</Typography.Title>
          <Typography.Text type="secondary">Quản lý tài khoản, thông tin đăng nhập và thiết bị liên kết.</Typography.Text>
        </div>
        {canManage && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm tài khoản</Button>}
      </div>

      <ManagementPanel title="Danh sách tài khoản" total={pageData.totalElements} filters={<div className="filter-bar">
        <Input.Search
          aria-label="Tìm tài khoản"
          allowClear
          placeholder="Mã, số tài khoản, ngân hàng, chủ tài khoản..."
          value={filters.query}
          onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
          onSearch={search}
          style={{ maxWidth: 360 }}
        />
        <Select
          aria-label="Lọc trạng thái"
          value={filters.active}
          onChange={(active) => {
            const next = { ...filters, active };
            setFilters(next);
            load(0, pageData.size, next);
          }}
          style={{ width: 160 }}
        >
          <Select.Option value="">Tất cả trạng thái</Select.Option>
          <Select.Option value={true}>Đang sử dụng</Select.Option>
          <Select.Option value={false}>Ngừng sử dụng</Select.Option>
        </Select>
        <Select aria-label="Lọc ngân hàng" placeholder="Tất cả ngân hàng" allowClear value={filters.bankName || undefined}
          onChange={bankName => { const next = { ...filters, bankName: bankName || "" }; setFilters(next); load(0, pageData.size, next); }} style={{ width: 180 }}>
          {bankNames.map(bank => <Select.Option key={bank} value={bank}>{bank}</Select.Option>)}
        </Select>
        <Select
          aria-label="Lọc loại tài khoản"
          value={filters.accountType}
          onChange={(accountType) => {
            const next = { ...filters, accountType };
            setFilters(next);
            load(0, pageData.size, next);
          }}
          style={{ width: 190 }}
        >
          <Select.Option value="">Tất cả loại tài khoản</Select.Option>
          {accountTypes.filter((item) => item.status).map((item) => (
            <Select.Option key={item.id} value={item.parCode}>{item.parName}</Select.Option>
          ))}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={() => load(pageData.page, pageData.size)}>Tải lại</Button>
      </div>}>

      {loadError && <Alert className="page-alert" type="error" showIcon message={loadError} action={<Button size="small" onClick={() => load(pageData.page, pageData.size)}>Thử lại</Button>} />}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={pageData.content}
        loading={loading}
        locale={{ emptyText: "Chưa có tài khoản ngân hàng" }}
        scroll={{ x: 920 }}
        pagination={{
          current: pageData.page + 1,
          pageSize: pageData.size,
          total: pageData.totalElements,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total) => `${total} tài khoản`,
        }}
        onChange={(pagination) => load(pagination.current - 1, pagination.pageSize)}
      />
      </ManagementPanel>

      <Modal className="upgrade-modal" width={610} title="Chi tiết tài khoản" open={Boolean(viewing)} onCancel={() => setViewing(null)} footer={<Button onClick={() => setViewing(null)}>Đóng</Button>} destroyOnClose>
        {viewing && <><AccountDetails account={viewing} currentUser={currentUser} />
          <div className="form-section-heading">Thông tin quản lý</div><div className="detail-grid">
            <div><span>Tên gợi nhớ</span><strong>{viewing.nickname || "—"}</strong></div>
            <div><span>Loại tài khoản</span><strong>{accountTypeName(viewing.accountType)}</strong></div>
            <div><span>Tình trạng tài khoản</span><strong>{accountStatuses.find((item) => item.parCode === viewing.accountStatus)?.parName || viewing.accountStatus || "—"}</strong></div>
            <div><span>Số dư đầu kỳ</span><strong>{MONEY.format(viewing.openingBalance)}</strong></div>
            <div><span>Ngày bắt đầu quản lý</span><strong>{viewing.managementStartDate ? DATE.format(new Date(`${viewing.managementStartDate}T00:00:00`)) : "—"}</strong></div>
          </div></>}
      </Modal>

      <Modal
        className="upgrade-modal account-create-modal" width={1240}
        title={editing ? "Cập nhật tài khoản" : "Thêm tài khoản"}
        open={modalOpen}
        onCancel={() => closeModal()}
        onOk={() => form.submit()}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving || detailLoading}
        okButtonProps={{ disabled: saving || detailLoading }}
        cancelButtonProps={{ disabled: saving || detailLoading }}
        closable={!saving && !detailLoading}
        maskClosable={!saving && !detailLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false} disabled={saving || detailLoading}>
          <section className="account-form-section">
            <div className="account-section-heading"><BankOutlined /><div><strong>Thông tin tài khoản</strong><small>Thông tin cơ bản của tài khoản</small></div></div>
            <div className="account-form-grid">
              <Form.Item label="Mã nội bộ" name="internalCode" rules={[{ required: true, message: "Nhập mã nội bộ" }]}><Input maxLength={100} placeholder="Nhập mã nội bộ" /></Form.Item>
              <Form.Item label="Ngân hàng" name="bankName" rules={[{ required: true, message: "Nhập tên ngân hàng" }]}><Input maxLength={255} placeholder="Nhập tên ngân hàng" /></Form.Item>
              <Form.Item label="Phân loại" name="accountType">
                <Select allowClear placeholder="Chọn phân loại">
                  {accountTypes.filter((item) => item.status || item.parCode === form.getFieldValue("accountType")).map((item) => (
                    <Select.Option key={item.id} value={item.parCode} disabled={!item.status}>{item.parName}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Chủ tài khoản" name="accountHolder" rules={[{ required: true, message: "Nhập chủ tài khoản" }]}><Input maxLength={255} placeholder="Nhập tên chủ tài khoản" /></Form.Item>
              <Form.Item label="Số tài khoản" name="accountNumber" rules={[{ required: true, message: "Nhập số tài khoản" }]}><Input maxLength={100} autoComplete="off" placeholder="Nhập số tài khoản" /></Form.Item>
              <Form.Item label="Tình trạng tài khoản" name="accountStatus" rules={[{ required: true, message: "Chọn tình trạng tài khoản" }]}>
                <Select placeholder="Chọn tình trạng" options={accountStatuses.map((item) => ({ value: item.parCode, label: item.parName, disabled: !item.status }))} />
              </Form.Item>
              <Form.Item label="Số thẻ" name="cardNumber" extra={editing?.hasCardNumber ? "Để trống để giữ số thẻ đã lưu" : undefined}><Input maxLength={100} autoComplete="off" placeholder="Nhập số thẻ" /></Form.Item>
              <Form.Item label="Mã bí mật" name="secretCode" extra={editing?.hasSecretCode ? "Để trống để giữ mã đã lưu; không nhập CVV/CVC của thẻ." : "Mã dùng để lưu trữ; không nhập CVV/CVC của thẻ."}><Input maxLength={255} autoComplete="off" placeholder="Nhập mã bí mật" /></Form.Item>
              <Form.Item label="Thời hạn thẻ (Visa)" name="cardExpiry"><DatePicker format="DD/MM/YYYY" placeholder="Chọn ngày hết hạn" style={{ width: "100%" }} /></Form.Item>
            </div>
          </section>
          <section className="account-form-section">
            <div className="account-section-heading"><SafetyOutlined /><div><strong>Thông tin đăng nhập</strong><small>Thông tin đăng nhập được lưu trữ</small></div></div>
            <div className="account-form-grid account-form-grid-two">
              <Form.Item label="Tên đăng nhập cũ" name="oldUsername"><Input maxLength={255} autoComplete="off" placeholder="Nhập tên đăng nhập cũ" /></Form.Item>
              {canCredentials && <Form.Item label="Pass cũ" name="oldPassword" extra={editing ? "Để trống để giữ mật khẩu đã lưu" : undefined}><Input.Password maxLength={500} autoComplete="new-password" placeholder="Nhập mật khẩu cũ" /></Form.Item>}
              <Form.Item label="Tên đăng nhập mới" name="newUsername"><Input maxLength={255} autoComplete="off" placeholder="Nhập tên đăng nhập mới" /></Form.Item>
              {canCredentials && <Form.Item label="Pass mới" name="newPassword" extra={editing ? "Để trống để giữ mật khẩu đã lưu" : undefined}><Input.Password maxLength={500} autoComplete="new-password" placeholder="Nhập mật khẩu mới" /></Form.Item>}
            </div>
          </section>
          <section className="account-form-section">
            <div className="account-section-heading"><SettingOutlined /><div><strong>Thông tin quản lý</strong><small>Cấu hình và thông tin quản lý tài khoản</small></div></div>
            <div className="account-form-grid">
              <Form.Item label="Tên gợi nhớ" name="nickname"><Input maxLength={255} placeholder="Nhập tên gợi nhớ" /></Form.Item>
              <Form.Item label="Loại tiền" name="currency"><Select><Select.Option value="VND">VND</Select.Option></Select></Form.Item>
              <Form.Item label="Số dư đầu kỳ" name="openingBalance" rules={[{ required: true, message: "Nhập số dư đầu kỳ" }, { type: "integer", min: 0, max: Number.MAX_SAFE_INTEGER, message: "Số dư phải là số nguyên VND hợp lệ" }]}><InputNumber min={0} max={Number.MAX_SAFE_INTEGER} precision={0} style={{ width: "100%" }} /></Form.Item>
              <Form.Item label="CCCD" name="citizenId" extra={editing?.hasCitizenId ? "Để trống để giữ CCCD đã lưu" : undefined}><Input maxLength={100} autoComplete="off" placeholder="Nhập số CCCD" /></Form.Item>
              <Form.Item label="Ngày hết hạn CCCD" name="citizenIdExpiry"><DatePicker format="DD/MM/YYYY" placeholder="Chọn ngày hết hạn" style={{ width: "100%" }} /></Form.Item>
              <Form.Item label="Ngày bắt đầu quản lý" name="managementStartDate" rules={[{ required: true, message: "Chọn ngày bắt đầu" }]}><DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} /></Form.Item>
              <Form.Item label="Hệ thống" name="systemName"><Input maxLength={255} placeholder="Nhập hệ thống" /></Form.Item>
              <Form.Item label="Chi nhánh" name="branch"><Input maxLength={255} placeholder="Nhập chi nhánh" /></Form.Item>
              <Form.Item label="Ngày bắt đầu SD" name="usageStartDate"><DatePicker format="DD/MM/YYYY" placeholder="Chọn ngày bắt đầu" style={{ width: "100%" }} /></Form.Item>
              <Form.Item label="Email" name="email" rules={[{ type: "email", message: "Email không hợp lệ" }]}><Input maxLength={255} placeholder="Nhập email" /></Form.Item>
              <Form.Item label="Nhà cung cấp" name="provider"><Input maxLength={255} placeholder="Nhập nhà cung cấp" /></Form.Item>
              <Form.Item label="Ngày đi" name="departureDate"><DatePicker format="DD/MM/YYYY" placeholder="Chọn ngày đi" style={{ width: "100%" }} /></Form.Item>
              <Form.Item className="account-form-wide" label="Thông tin" name="information"><Input.TextArea rows={2} placeholder="Nhập thông tin thêm (ghi chú, mô tả, ...)" /></Form.Item>
            </div>
          </section>
        </Form>
      </Modal>
    </div>
  );
}

